/**
 * ConsentAwareAnalytics — client-side page_view, engagement, and delegated
 * measurement events. Fires only when Analytics consent is granted.
 *
 * Guarantees:
 *  - Exactly one page_view per SPA route (including the current route when
 *    consent is granted AFTER mount).
 *  - No analytics-related session/local storage writes before consent.
 *  - No raw query strings or full document.referrer; only safe origin +
 *    allowlisted UTM fields with strict validation.
 *  - Ad-click identifiers (gclid/fbclid) surface as boolean flags only when
 *    Advertising is granted.
 */
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { trackEvent } from "@/lib/analytics";
import {
  getConsent,
  initConsent,
  subscribeConsent,
  trackHubSpotPageView,
  dataLayerEventsAllowed,
} from "@/lib/consent";
import { applySharedPageContext, buildPageContext, safePagePath } from "@/lib/page-context";
import { initAdsTracking } from "@/lib/ads-tracking";
import { initCallTracking, refreshCallTracking } from "@/lib/call-tracking";

import { isPublicTrackableRoute } from "@/lib/public-routes";
import { pushMarketingEvent } from "@/lib/marketing-datalayer";
import { installVendorRouteBoundary } from "@/lib/vendor-route-boundary";
import { captureLeadAttribution, clearLeadAttribution, attributionAllowed } from "@/lib/lead-attribution";

const LANDING_KEY = "mb-landing-fired";

// utm_source, utm_medium, utm_campaign, utm_content, utm_term
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"] as const;
// Strict validation: 1-100 chars, alnum / dot / dash / underscore / space only.
const UTM_RE = /^[A-Za-z0-9._\- ]{1,100}$/;
// Reject anything resembling contact info.
const EMAIL_RE = /@/;
const PHONE_RE = /(?:\+?\d[\s\-().]*){7,}/;

const safeUtm = (search: string): Record<string, string> => {
  const out: Record<string, string> = {};
  try {
    const params = new URLSearchParams(search);
    for (const k of UTM_KEYS) {
      const v = params.get(k);
      if (!v) continue;
      if (!UTM_RE.test(v)) continue;
      if (EMAIL_RE.test(v) || PHONE_RE.test(v)) continue;
      out[k] = v;
    }
  } catch { /* noop */ }
  return out;
};

const adClickFlags = (search: string, advertising: boolean): Record<string, boolean> => {
  if (!advertising) return {};
  const flags: Record<string, boolean> = {};
  try {
    const params = new URLSearchParams(search);
    if (params.has("gclid")) flags.has_gclid = true;
    if (params.has("fbclid")) flags.has_fbclid = true;
    if (params.has("msclkid")) flags.has_msclkid = true;
    if (params.has("ttclid")) flags.has_ttclid = true;
  } catch { /* noop */ }
  return flags;
};

const safeReferrer = (): string | undefined => {
  try {
    const r = document.referrer;
    if (!r) return undefined;
    const u = new URL(r);
    if (u.origin === window.location.origin) return undefined;
    return u.hostname; // hostname only — no path, no query
  } catch { return undefined; }
};

/** Sections of the site → page-category events. Route/segment boundary only. */
const SERVICE_SEGMENTS = new Set([
  "solar", "roofing", "electrical", "construction", "battery-storage", "ev-chargers",
  "commercial", "smart-home", "magic-care", "main-panel-upgrades", "solar-installation",
  "solar-service-maintenance", "solar-inspections", "roof-repairs", "roof-maintenance",
  "roof-inspections", "removal-reinstall", "general-contracting", "panel-cleaning-quote",
  "wildfire-sprinkler", "wildfire-rebuild", "electrical-services", "solar-rescue",
]);
const BRAND_SEGMENTS = new Set(["enphase", "tesla-energy", "franklin"]);

const pageCategory = (path: string): { event: string; params: Record<string, string> } | null => {
  const seg = path.split("/").filter(Boolean)[0];
  if (!seg) return null;
  if (seg === "packages") return { event: "package_page_view", params: { section: "packages" } };
  if (BRAND_SEGMENTS.has(seg)) return { event: "brand_page_view", params: { section: "brand", brand: seg } };
  if (SERVICE_SEGMENTS.has(seg)) return { event: "service_page_view", params: { section: "service", service: seg } };
  return null;
};

const CALC_ROUTES: Record<string, string> = {
  // "/roofing-calculator" is intentionally absent: the roofing ad landing
  // distinguishes a landing view (below) from an actual start, which
  // MagicFlow emits on the first meaningful calculator interaction.
  "/ev-calculator": "ev_charger",
  "/mpu-calculator": "mpu",
  "/electrical-estimator": "electrical",
  "/solar-calculator": "solar",
  "/removal-reinstall-calculator": "removal_reinstall",
  "/panel-cleaning-quote": "panel_cleaning",
};

/** Sanitize a click destination. Never returns raw query/hash/PII. */
const safeDestination = (href: string): string | undefined => {
  if (!href) return undefined;
  // Never send tel/sms/mailto/javascript/data.
  if (/^(tel:|sms:|mailto:|javascript:|data:)/i.test(href)) return undefined;
  try {
    const url = new URL(href, window.location.origin);
    if (url.protocol !== "http:" && url.protocol !== "https:") return undefined;
    if (url.origin === window.location.origin) return url.pathname;
    return url.hostname;
  } catch { return undefined; }
};

const ConsentAwareAnalytics = () => {
  const { pathname, search } = useLocation();
  const lastPath = useRef<string>("");
  const engagementFired = useRef(false);
  const scroll50Fired = useRef(false);
  const scroll90Fired = useRef(false);
  const engagementTimer = useRef<number | null>(null);
  // Remember which route has already produced a page_view so we can also
  // emit it when consent is granted AFTER the effect ran.
  const pageViewedFor = useRef<string>("");
  const marketingViewedFor = useRef<string>("");

  // Meta page signal is independent of GA4: an advertising-only visitor still
  // gets it, and an analytics-only visitor never does.
  const fireMarketingPageView = (path: string, srch: string) => {
    if (!isPublicTrackableRoute(path)) return;
    if (marketingViewedFor.current === path + srch) return;
    // Curated Meta event, on the isolated marketing data layer, exactly once
    // per route and only while advertising is currently allowed. Never queued
    // during a denied period, never replayed. Google's gtag page_view cannot
    // reach a Meta trigger any more, so this is the single Meta page signal we
    // own. (HubSpot's separately-owned Meta pixel configuration is unchanged,
    // so Meta is not claimed to be fully de-duplicated end to end.)
    // The event NAME must match the published container's own triggers
    // (GTM-WGFZFB8R v6 fires its Meta tags on `page_view` and `new_quote`).
    // The isolation is the separate `marketingDataLayer`, not a renamed event:
    // a curated `mb_page_view` matched no trigger at all.
    // Payload shape is dictated by the DEPLOYED container: GTM-WGFZFB8R v6
    // tag 12 listens on the exact event name `page_view` and sends
    // fbq('track','PageView',{page_location,page_path,page_title}), so all
    // three variables must be present on the pushed event. The base tag
    // (tag 10, gtm.init) owns fbq('init', …) and sends NO PageView, so this is
    // the only Meta page signal — the app never inits and never double-fires.
    const ctx = buildPageContext(path);
    if (
      pushMarketingEvent(
        "page_view",
        {
          page_path: ctx.page_path,
          page_title: ctx.page_title,
          ...(ctx.page_location ? { page_location: ctx.page_location } : {}),
        },
        dataLayerEventsAllowed(),
      )
    ) {
      marketingViewedFor.current = path + srch;
    }
  };

  const firePageViewFor = (path: string, srch: string) => {
    fireMarketingPageView(path, srch);
    if (!getConsent().analytics) return;
    // Vendors never observe a private or unknown route.
    if (!isPublicTrackableRoute(path)) return;
    if (pageViewedFor.current === path + srch) return;

    const utm = safeUtm(srch);
    const ads = adClickFlags(srch, getConsent().advertising);
    const referrer_host = safeReferrer();
    // Commit the de-duplication key ONLY once the page_view actually reached
    // the gtag queue with GA4 configured; otherwise a later attempt retries.
    const queued = trackEvent("page_view", {
      page_path: path,
      ...(referrer_host ? { referrer_host } : {}),
      ...utm,
      ...ads,
    });
    if (!queued) return;
    pageViewedFor.current = path + srch;
    // Landing (first page_view this session) — write session key AFTER the
    // event fires so denied users never accumulate storage.
    try {
      if (!sessionStorage.getItem(LANDING_KEY)) {
        trackEvent("landing_page", { page_path: path, ...utm });
        sessionStorage.setItem(LANDING_KEY, "1");
      }
    } catch { /* noop */ }

    const cat = pageCategory(path);
    if (cat) trackEvent(cat.event, { page_path: path, ...cat.params });

    const calc = CALC_ROUTES[path];
    if (calc) trackEvent("calculator_start", { calculator: calc, page_path: path });
    // Roofing ad landing: a route view is a LANDING, never a start. The
    // start event fires from MagicFlow on the visitor's first meaningful
    // calculator interaction (address entry/selection or the no-map
    // fallback), so ad traffic that bounces is not counted as a start.
    if (path === "/roofing-calculator" || path === "/free-roof-estimate") {
      trackEvent("calculator_landing_view", { calculator: "roofing", page_path: path });
    }
  };

  const startEngagementTimer = (path: string) => {
    if (engagementTimer.current) window.clearTimeout(engagementTimer.current);
    engagementTimer.current = window.setTimeout(() => {
      if (engagementFired.current) return;
      if (!getConsent().analytics) return;
      engagementFired.current = true;
      trackEvent("engaged_30_seconds", { page_path: path });
    }, 30_000);
  };

  // page_view + counters on route change.
  // Google Ads tag: initialize once. Loads immediately when advertising
  // consent is already granted (landing-time click attribution) and on a
  // later grant; never while denied or under GPC.
  useEffect(() => {
    // Consent FIRST. The banner is lazily mounted, so without this the very
    // first route effect ran against the uninitialized all-denied default:
    // a returning visitor's stored grant was ignored, stored campaign context
    // was cleared, and a freshly tagged landing was missed. `initConsent` is
    // idempotent, so the banner's own call is still a no-op.
    initConsent();
    // Full document navigation at the public→private boundary, so vendors that
    // are already loaded never see a private URL.
    installVendorRouteBoundary();
    initAdsTracking();
    // Call-from-website conversion: same advertising permission, same public
    // route boundary, same base tag. Never fires a conversion from a click.
    initCallTracking();
  }, []);


  useEffect(() => {
    const key = pathname + search;
    if (lastPath.current === key) return;
    lastPath.current = key;

    engagementFired.current = false;
    scroll50Fired.current = false;
    scroll90Fired.current = false;
    pageViewedFor.current = "";
    marketingViewedFor.current = "";
    if (engagementTimer.current) window.clearTimeout(engagementTimer.current);

    // Refresh the sanitized page defaults AND every already-configured Google
    // destination before any event for this route, so the automatic
    // collectors that remain enabled (scroll, video, user_engagement) report
    // the CURRENT safe page rather than the previous one.
    applySharedPageContext(pathname);

    // UTM-only campaign context for a later FINAL lead. Requires advertising
    // permission now; a tagged landing replaces the context, untagged internal
    // navigation preserves it, and a denial clears it.
    captureLeadAttribution(pathname, search);

    firePageViewFor(pathname, search);
    // New route: pick up call links this page rendered, or restore the real
    // number when the route is not one a vendor may observe.
    refreshCallTracking();

    // Consent-aware HubSpot SPA page view. No-ops when consent denied and
    // skips the path that HubSpot's own loader auto-fires on first load.
    // HubSpot receives the sanitized path only — never the query string.
    if (isPublicTrackableRoute(pathname)) trackHubSpotPageView(safePagePath(pathname));

    // Only start engagement timer if analytics is currently granted; otherwise
    // it starts when consent later transitions to granted.
    if (getConsent().analytics) startEngagementTimer(pathname);

    return () => {
      if (engagementTimer.current) window.clearTimeout(engagementTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, search]);

  // React to consent changes: fire retroactive page_view for CURRENT route,
  // start engagement timer at grant, stop it on revocation. Also clear the
  // landing session key on revocation so a later grant is not treated as
  // mid-session (and never accumulates storage during denial).
  useEffect(() => {
    let prevAnalytics = getConsent().analytics;
    let prevAttribution = attributionAllowed();
    return subscribeConsent((s) => {
      if (s.analytics && !prevAnalytics) {
        // Late grant: silently consume any thresholds already crossed BEFORE
        // consent so they never fire retroactively. Only future crossings emit.
        try {
          const h = document.documentElement;
          const max = h.scrollHeight - h.clientHeight;
          if (max > 0) {
            const pct = window.scrollY / max;
            if (pct >= 0.5) scroll50Fired.current = true;
            if (pct >= 0.9) scroll90Fired.current = true;
          }
        } catch { /* noop */ }
      }
      // Meta is INDEPENDENT of Analytics: an advertising-only grant must still
      // produce exactly one page view for the CURRENT route. `fireMarketingPageView`
      // self-gates on advertising permission and on its own per-route key, so
      // calling it on every consent callback cannot duplicate.
      fireMarketingPageView(pathname, search);
      if (!dataLayerEventsAllowed()) {
        // Advertising revoked (or GPC / device opt-out): allow exactly one
        // current-route Meta page view if permission ever returns.
        marketingViewedFor.current = "";
      }
      if (s.analytics) {
        firePageViewFor(pathname, search);
        // HubSpot only tracks here on a real denied→granted transition.
        // Route changes are handled by the route effect; subscribeConsent
        // immediately invokes listeners and also re-runs when pathname/search
        // changes, so tracking on every truthy callback duplicates SPA views.
        if (!prevAnalytics && isPublicTrackableRoute(pathname)) {
          trackHubSpotPageView(safePagePath(pathname));
        }
        if (!engagementFired.current && !engagementTimer.current) {
          startEngagementTimer(pathname);
        }
      } else {
        if (engagementTimer.current) {
          window.clearTimeout(engagementTimer.current);
          engagementTimer.current = null;
        }
        // On explicit revocation, purge analytics-only session storage and
        // reset the page-view guard so a later re-grant may emit exactly one
        // current-route page_view.
        if (prevAnalytics) {
          try { sessionStorage.removeItem(LANDING_KEY); } catch { /* noop */ }
          pageViewedFor.current = "";
          engagementFired.current = false;
          scroll50Fired.current = false;
          scroll90Fired.current = false;
        }
      }
      // Advertising permission just became available: capture the CURRENT
      // url's campaign context now. Nothing earlier is replayed — only the
      // page the visitor is on at the moment they granted permission.
      const nowAllowed = attributionAllowed();
      if (nowAllowed && !prevAttribution) {
        captureLeadAttribution(window.location.pathname, window.location.search);
      }
      // Advertising withdrawal / GPC / device opt-out clears stored campaign
      // context immediately.
      if (!nowAllowed) clearLeadAttribution();
      prevAttribution = nowAllowed;
      prevAnalytics = s.analytics;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, search]);

  // Scroll depth — only observed while analytics is granted. Denied-period
  // scrolls do NOT arm the flags; a later grant measures future crossings only
  // (already-crossed thresholds are silently consumed on the grant transition).
  useEffect(() => {
    const onScroll = () => {
      if (!getConsent().analytics) return;
      const h = document.documentElement;
      const max = h.scrollHeight - h.clientHeight;
      if (max <= 0) return;
      const pct = window.scrollY / max;
      if (!scroll50Fired.current && pct >= 0.5) {
        scroll50Fired.current = true;
        trackEvent("scroll_50", { page_path: pathname });
      }
      if (!scroll90Fired.current && pct >= 0.9) {
        scroll90Fired.current = true;
        trackEvent("scroll_90", { page_path: pathname });
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [pathname]);

  // Delegated CTA click listener — click_to_call/text/email + primary_cta_click.
  useEffect(() => {
    const recent = new Map<string, number>();
    const onClick = (e: MouseEvent) => {
      const t = e.target as HTMLElement | null;
      if (!t) return;
      const a = t.closest("a,button") as HTMLElement | null;
      if (!a) return;
      const href = a.getAttribute("href") || "";
      let event: string | null = null;
      const params: Record<string, string> = { page_path: window.location.pathname };
      if (href.startsWith("tel:")) event = "click_to_call";
      else if (href.startsWith("sms:")) event = "click_to_text";
      else if (href.startsWith("mailto:")) event = "click_to_email";
      else if (a.dataset.cta === "primary" || a.getAttribute("data-analytics-cta") === "primary") {
        event = "primary_cta_click";
        const label = a.getAttribute("data-analytics-label") || a.getAttribute("aria-label") || "";
        if (label && label.length <= 60 && !EMAIL_RE.test(label) && !PHONE_RE.test(label)) {
          params.label = label;
        }
        const dest = safeDestination(href);
        if (dest) params.destination = dest;
      }
      if (!event) return;
      const dedupeKey = `${event}:${href}:${window.location.pathname}`;
      const now = Date.now();
      const last = recent.get(dedupeKey);
      if (last && now - last < 750) return;
      recent.set(dedupeKey, now);
      trackEvent(event, params);
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);

  // Delegated form validation errors → form_validation_error (field name/type only)
  useEffect(() => {
    const onInvalid = (e: Event) => {
      const t = e.target as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null;
      if (!t || !("name" in t)) return;
      trackEvent("form_validation_error", {
        page_path: window.location.pathname,
        field_name: (t.name || t.id || "unknown").slice(0, 60),
        field_type: (t as HTMLInputElement).type || t.tagName.toLowerCase(),
      });
    };
    document.addEventListener("invalid", onInvalid, true);
    return () => document.removeEventListener("invalid", onInvalid, true);
  }, []);

  return null;
};

export default ConsentAwareAnalytics;

